/**
 * Save-conflict dialogs state (Faz 9.1, OCS `SaveFailure` taksonomisi).
 *
 * Two blocking asks the save pipeline can raise before/while writing:
 * - External change: the on-disk fingerprint no longer matches what this
 *   session last saved → "Save anyway / Cancel" (OCS ExternalChange modal).
 * - File in use: a sharing violation survived the retry window → "Retry /
 *   Cancel" (OCS FileInUse modal).
 *
 * Same deferred-promise pattern as useUnsavedChangesStore: the save flow
 * awaits `request*`, the mounted dialog resolves it via `close*`.
 */

import { create } from 'zustand';

interface SaveConflictState {
    /** File name shown in the external-change dialog; null = closed. */
    externalFileName: string | null;
    /** File name shown in the file-in-use dialog; null = closed. */
    inUseFileName: string | null;
    requestExternalChange: (fileName: string) => Promise<boolean>;
    requestFileInUse: (fileName: string) => Promise<boolean>;
    /** Resolves the pending external-change ask (true = overwrite anyway). */
    closeExternalChange: (proceed: boolean) => void;
    /** Resolves the pending file-in-use ask (true = try the write again). */
    closeFileInUse: (retry: boolean) => void;
}

export const useSaveConflictStore = create<SaveConflictState>()((set, get) => ({
    externalFileName: null,
    inUseFileName: null,
    requestExternalChange: (fileName) => {
        if (get().externalFileName !== null) return Promise.resolve(false);
        return new Promise<boolean>((resolve) => {
            externalResolver = resolve;
            set({ externalFileName: fileName });
        });
    },
    requestFileInUse: (fileName) => {
        if (get().inUseFileName !== null) return Promise.resolve(false);
        return new Promise<boolean>((resolve) => {
            inUseResolver = resolve;
            set({ inUseFileName: fileName });
        });
    },
    closeExternalChange: (proceed) => {
        set({ externalFileName: null });
        externalResolver?.(proceed);
        externalResolver = null;
    },
    closeFileInUse: (retry) => {
        set({ inUseFileName: null });
        inUseResolver?.(retry);
        inUseResolver = null;
    },
}));

let externalResolver: ((proceed: boolean) => void) | null = null;
let inUseResolver: ((retry: boolean) => void) | null = null;
